<template>

  <m-pay-layout>

    <v-layout justify-center align-top class="mx-2">
      <v-flex grow>

        <m-pay-generic-paging-table
          :headers="headers"
          :queryFunctionName="queries.transactions.name"
          :csvQueryFunctionName="queries.transactionsAsCsv.name"
          :itemStatusClasses="transactionStatusClasses"
          :cellClasses="cellClasses"
          @exportWithLimit="exportWithLimit"
        >
          <template slot-scope={item} slot="edit">
            <router-link :to="{ name: 'transaction', params: { transactionId: item.id } }">
              <v-tooltip bottom>
                    <span slot="activator">
                      <v-icon color="secondary">zoom_in</v-icon>
                    </span>
                <span>Transaction Details</span>
              </v-tooltip>
            </router-link>
          </template>
          <template slot-scope={item} slot="merchant">
            <router-link :to="{ name: 'merchant', params: { merchantId: item.merchantId } }">
              <v-tooltip bottom>
                    <span slot="activator">
                      <v-icon color="secondary">store</v-icon>
                    </span>
                <span>Merchant</span>
              </v-tooltip>
            </router-link>
          </template>
          <template slot-scope={item} slot="purchase">
            <router-link :to="{ name: 'purchase', params: { purchaseId: item.paymentRequestId } }">
              <v-tooltip bottom>
                    <span slot="activator">
                      <v-icon color="secondary">shopping_cart</v-icon>
                    </span>
                <span>Purchase</span>
              </v-tooltip>
            </router-link>
          </template>
          <template v-if="item.customerId != null" slot-scope={item} slot="customer">
            <router-link :to="{ name: 'customer', params: { customerId: item.customerId } }">
              <v-tooltip bottom>
                    <span slot="activator">
                      <v-icon color="secondary">person</v-icon>
                    </span>
                <span>Customer</span>
              </v-tooltip>
            </router-link>
          </template>
          <template v-if="item.recurringPaymentId" slot-scope={item} slot="recurringPayment">
            <router-link :to="{ name: 'recurring', query: { f_id: item.recurringPaymentId } }">
              <v-tooltip bottom>
                    <span slot="activator">
                      <v-icon color="secondary">autorenew</v-icon>
                    </span>
                <span>Recurring Payment</span>
              </v-tooltip>
            </router-link>
          </template>
          <template slot-scope={item} slot="fullName">
            {{ item.customerFirstName }} {{ item.customerLastName }}
          </template>
          <template slot-scope="{item}" slot="purchaseId">
            <span
              @click="redirectToPurchase(item.purchaseId)">
              {{ item.purchaseId }}
            </span>
          </template>
          <template slot-scope={value} slot="externalErrorDescription">
            <m-pay-tooltip-copy-button :value="value" :display-length="50">
            </m-pay-tooltip-copy-button>
          </template>
        </m-pay-generic-paging-table>
        <m-pay-transaction-export-limit-dialog
          title="Export transactions to csv file"
          description="Please set transactions period from"
          max-width="500"
          :show="ExportLimitToCsvModel.show"
          :export-date-from="ExportLimitToCsvModel.dateFrom"
          :export-date-to="ExportLimitToCsvModel.dateTo"
          @updateDateFrom="updateDateFrom"
          @updateDateTo="updateDateTo"
          @export="exportCsv"
          @cancel="hideExportDialog"/>
      </v-flex>
    </v-layout>
  </m-pay-layout>

</template>

<script>
import moment from 'moment';
import axios from 'axios';
import MPayGenericPagingTable from './MPayGenericPagingTable';
import MPayStatus from './MPayStatus';
import roles from '../utils/user-roles';
import { transactionStatusClasses } from '../utils/statuses';
import MPayTooltipCopyButton from './MPayTooltipCopyButton';
import MPayTransactionExportLimitDialog from './MPayTransactionExportLimitDialog';
import queries from '../utils/queries';

export default {
  name: 'MPayTransactions',

  components: {
    MPayGenericPagingTable,
    MPayStatus,
    MPayTooltipCopyButton,
    MPayTransactionExportLimitDialog,
  },

  data: () => ({
    headers: [
      { text: '', value: 'edit', sortable: false, filterable: false },
      { text: '', value: 'merchant', sortable: false, filterable: false, role: roles.READ_ANY_USER },
      { text: '', value: 'purchase', sortable: false, filterable: false },
      { text: '', value: 'customer', sortable: false, filterable: false, role: roles.READ_ANY_USER },
      { text: '', value: 'recurringPayment', sortable: false, filterable: false },
      { text: 'Payer', value: 'fullName', sortable: false, filterable: false, width: 150, role: roles.READ_ANY_USER },
      { text: 'Amount', value: 'amount', align: 'right' },
      { text: 'Currency', value: 'currency' },
      { text: 'Date', value: 'date', width: 155 },
      { text: 'Merchant', value: 'merchantName', width: 200, role: roles.READ_ANY_USER },
      { text: 'Status', value: 'status' },
      { text: 'Description', value: 'description', width: 250 },
      { text: 'Purchase Id', value: 'purchaseId', width: 300 },
      { text: 'Auth Ref Nr', value: 'authRefNr', sortable: false, width: 50 },
      { text: 'Tr Approval Id', value: 'trApprovalId', sortable: false, width: 50 },
      { text: 'Card', value: 'cardPan', width: 50, sortable: false, filterable: true },
      { text: 'Account Statement Ref', value: 'accountStatementReference', sortable: false, filterable: true },
      { text: 'Batch Id', value: 'batchId', sortable: false, filterable: true },
      { text: 'Error type', value: 'externalErrorType', sortable: true, filterable: true, roles: [roles.READ_ANY_PURCHASE], width: 50 },
      { text: 'Error code', value: 'externalErrorCode', sortable: true, filterable: true, roles: [roles.READ_ANY_PURCHASE], width: 30 },
      { text: 'Error', value: 'externalErrorDescription', filterable: true, roles: [roles.READ_ANY_PURCHASE], width: 200 },
      { text: 'xid', value: 'xid', filterable: true, roles: [roles.READ_ANY_PURCHASE] },
    ],
    cellClasses: {
      edit: 'pl-3 pr-1',
      merchant: 'pl-0 pr-1',
      purchase: 'pl-0 pr-1',
      customer: 'pl-0 pr-1',
      recurringPayment: 'pl-0 pr-1',
    },
    transactionStatusClasses,
    ExportLimitToCsvModel: {
      show: false,
      dateFrom: null,
      dateTo: null,
      pagination: {},
      filtering: {},
    },
  }),

  methods: {
    hideExportDialog() {
      this.ExportLimitToCsvModel.show = false;
    },
    exportCsv() {
      axios.get(queries.transactionsLimitAsCsv(this.ExportLimitToCsvModel.pagination,
        this.ExportLimitToCsvModel.filtering, this.ExportLimitToCsvModel.dateFrom,
        this.ExportLimitToCsvModel.dateTo)).then((response) => {
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `transactions_${this.getDateWithIsoFormat()}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      });
    },
    updateDateFrom(value) {
      this.ExportLimitToCsvModel.dateFrom = value;
    },
    updateDateTo(value) {
      this.ExportLimitToCsvModel.dateTo = value;
    },
    exportWithLimit(pagination, filtering) {
      this.ExportLimitToCsvModel.show = true;
      this.ExportLimitToCsvModel.dateFrom = this.getDateWithIsoFormat();
      this.ExportLimitToCsvModel.dateTo = this.getDateWithIsoFormat();
      this.ExportLimitToCsvModel.pagination = pagination;
      this.ExportLimitToCsvModel.filtering = filtering;
    },
    getDateWithIsoFormat() {
      return moment().format('YYYY-MM-DD');
    },
    redirectToPurchase(purchaseId) {
      if (purchaseId) {
        const url = `https://admin.klix.app/wizard/feed/?search=${purchaseId}`;
        window.open(url, '_blank');
      }
    },
  },
};
</script>



// WEBPACK FOOTER //
// src/components/MPayTransactions.vue